import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { faqPageSchema } from '@/utils/seo';
import type { FAQItem } from '../../types';
import { useI18n } from '@/i18n';

const musicFaqs: FAQItem[] = [
  { question: 'How do I find a suitable music tutor?', answer: 'Tell us the instrument, learner level, location, and preferred schedule. We will use those details to help connect you with a suitable music tutor.' },
  { question: 'Which instruments can I learn?', answer: 'The Music experience supports a growing range of instruments and vocal lessons. Submit your learning request and include the instrument or musical skill you want to study.' },
  { question: 'Are Music tutors verified?', answer: 'Music tutors submit their core profile and supporting information for review before approval. Their Music profile also records their instruments, teaching levels, and experience.' },
  { question: 'Can lessons be online or in person?', answer: 'Availability depends on each tutor. You can share your preferred lesson mode and location when submitting your request.' },
  { question: 'Can beginners register for lessons?', answer: 'Yes. Music Tutor Founder is designed for learners at different stages, including complete beginners, returning learners, exam candidates, and hobbyists.' },
  { question: 'How do I register as a Music tutor?', answer: 'Choose Register as Tutor, sign in or create your shared Tutor Founder account, then complete your Music teaching profile for review.' },
];

interface FAQSectionProps {
  variant?: 'academic' | 'music';
}

const FAQSection: React.FC<FAQSectionProps> = ({ variant = 'academic' }) => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const academicFaqs: FAQItem[] = [
    {
      question: isZh ? '我怎样在马来西亚找到合适的导师？' : 'How do I find a suitable tutor in Malaysia?',
      answer: isZh
        ? '你可以浏览导师列表，并按科目、地点、价格范围和教育程度进行筛选，也可以使用我们的 AI 配对系统快速找到符合需求的导师。大多数家长会在 24 小时内找到合适人选。'
        : 'You can browse our tutor listings, use advanced search filters by subject, location, price range, and education level, or use our AI matching system to instantly find tutors that fit your specific requirements. Most parents find a suitable tutor within 24 hours.',
    },
    {
      question: isZh ? 'Tutor Founder 上的导师都会经过验证吗？' : 'Are all tutors on Tutor Founder verified?',
      answer: isZh
        ? '会。每位导师在资料审核前都必须提交辅助文件或证书。若导师也提交有效身份证明文件（身份证、护照或驾照），通过后就能显示验证标记。'
        : 'Yes. Every tutor must submit a support document or certificate before their profile can be reviewed and approved. Tutors who also submit valid identification documents (IC, passport, or driving licence) can display a verification badge after approval.',
    },
    {
      question: isZh ? '马来西亚补习导师通常多少钱？' : 'How much does a tutor cost in Malaysia?',
      answer: isZh
        ? '平台上的导师收费通常约为每小时 RM30 至 RM150，视科目、教育程度和导师经验而定。你可在联络前先查看导师个人档案中的时薪，也可前往学费参考页面了解不同程度与科目的收费区间。'
        : 'Tutor rates on our platform typically range from RM30 to RM150 per hour depending on the subject, education level, and tutor experience. You can view each tutor\'s hourly rate on their profile before contacting them. Visit our Tuition Fee Reference page for a detailed breakdown by level and subject.',
    },
    {
      question: isZh ? 'Tutor Founder 的付款方式是怎样的？' : 'How does payment work on Tutor Founder?',
      answer: isZh
        ? '家长在预约课程时会向 Tutor Founder 付款。款项会被安全托管，导师则在每月底收到拨款。这能同时保障双方权益；若导师取消课程，家长有保障，而导师也能收到稳定的月度收入。完整详情请参阅我们的条款与付款政策。'
        : 'Parents pay lesson fees to Tutor Founder when they book sessions. Payments are held securely and tutors are paid at the end of each month. This protects both parties — parents are covered if a tutor cancels, and tutors receive reliable monthly payouts. See our Terms & Payments policy for full details.',
    },
    {
      question: isZh ? '我可以找线上课程导师吗？' : 'Can I get a tutor for online lessons?',
      answer: isZh
        ? '可以。平台上很多导师提供线上补习、上门补习或两者皆可。你可以在导师搜索页按授课方式筛选，找到符合你偏好形式的导师。'
        : 'Yes. Many tutors on our platform offer online lessons, physical home tuition, or both. You can filter by tuition type (online, physical, or both) on the tutor search page to find tutors that match your preferred format.',
    },
    {
      question: isZh ? '导师覆盖哪些科目和程度？' : 'What subjects and levels do tutors cover?',
      answer: isZh
        ? '我们的导师覆盖小学、初中（Form 1-3）、高中（Form 4-5 / IGCSE / O-Level）、Form 6 / A-Level，以及国际学校课程。热门科目包括数学、科学、英文、马来文、高等数学、化学、物理和生物。'
        : 'Our tutors cover a wide range of subjects across Primary, Lower Secondary (Form 1–3), Upper Secondary (Form 4–5 / IGCSE / O-Level), Form 6 / A-Level, and International school curricula. Popular subjects include Mathematics, Science, English, Bahasa Malaysia, Additional Mathematics, Chemistry, Physics, and Biology.',
    },
    {
      question: isZh ? '如果我对导师不满意怎么办？' : 'What if I\'m not satisfied with my tutor?',
      answer: isZh
        ? '你可以随时重新寻找并更换导师，没有长期绑约。我们建议一开始就清楚沟通期待，但如果不合适，我们也会支持你为孩子找到更匹配的导师。'
        : 'You can search for and switch to a different tutor at any time — there are no lock-in contracts. We encourage you to communicate your expectations clearly from the start, but we fully support finding the right match for your child.',
    },
    {
      question: isZh ? '如果导师无法继续上课怎么办？' : 'What happens if a tutor discontinues lessons?',
      answer: isZh
        ? '如果导师无法继续授课，Tutor Founder 会协助处理。视情况而定，我们可为未使用的预付课时安排退款，或协助重新配对导师。联络支援团队后，我们会尽快跟进。'
        : 'If a tutor is unable to continue, Tutor Founder will help resolve the situation. Depending on the case, we may process a refund for unused prepaid lessons or assist in finding a replacement tutor. Contact our support team and we will handle it promptly.',
    },
  ];
  const faqs = variant === 'music' ? musicFaqs : academicFaqs;

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema(faqs))}
        </script>
      </Helmet>

      <section className="bg-white py-14 md:py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-8 md:mb-12 text-center"
          >
            <h2 className="mb-3 text-4xl font-bold tracking-tight text-gray-900 md:text-5xl">
              {variant === 'music' ? 'You may want to know' : isZh ? '你可能想知道' : 'You may want to know'}
            </h2>
          </motion.div>

          <motion.div
            className="space-y-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.04)]"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="flex w-full cursor-pointer items-center justify-between p-5 text-left transition-colors duration-200 hover:bg-gray-50"
                >
                  <span className="pr-8 font-semibold text-gray-900">{faq.question}</span>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className={`h-5 w-5 flex-shrink-0 ${variant === 'music' ? 'text-accent' : 'text-primary'}`} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <motion.div
                        initial={{ y: -10 }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-gray-200 px-5 pb-5 pt-4 text-sm leading-relaxed text-gray-600"
                      >
                        {faq.answer}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQSection;
